import { useStyles } from "./styles";
import { Grid } from "@mui/material";
import Button from "../../components/Button";
import Layout from "../../components/Layout";
import { Icon } from "@iconify/react";
import { useState, useEffect } from "react";
import CreateSessionModal from "../schoolplans/sessionmodal";
import CreateTermModal from "../schoolplans/termmodal";
import AddTeacherModal from "../employees/employeemodal";
import CreateClassModal from "../classes/classmodal/classmodal";
import { useNavigate } from "react-router-dom";
import AddGuardianModal from "../guardian/guardianmodal";
import { httprequest } from "../../data/api";
import ReactPlayer from "react-player";
import CreateSubjectModal from "../subjects/subjectmodal";
import PageLoader from "../../components/PageLoader";

const ONBOARDING = "/api/v1/onboarding-progress";

const OnboardingGuide = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState(null);
  const [isloading, setIsLoading] = useState(false);
  const [completedSteps, setCompletedSteps] = useState({});
  const [showVideo, setShowVideo] = useState(false);
  // eslint-disable-next-line no-console
  console.log("completedSteps", completedSteps);

  const getOnboarding = () => {
    setIsLoading(true);
    httprequest.get(ONBOARDING).then((res) => {
      setCompletedSteps(res?.data?.data);
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
      // toast.error(err?.response?.data);
    });
  };

  // Find the first incomplete step
  const findFirstIncompleteStepIndex = () => {
    const stepKeys = ["create_session", "create_term", "add_employees", "create_classes", "add_students", "add_guardians", "add_subjects"];

    for (let i = 0; i < stepKeys?.length; i++) {
      if (!(completedSteps || {})[stepKeys[i]]) {
        return i;
      }
    }

    return -1;
  };

  const firstIncompleteIndex = findFirstIncompleteStepIndex();

  const steps = [
    {
      id: "session",
      key: "create_session",
      title: "Create session",
      description: "Start by setting up a new academic session. This defines the time frame (e.g., 2024/2025) for all academic activities including terms, reports, and enrollments.",
      completed: completedSteps?.create_session,
      action: () => setActiveModal("session")
    },
    {
      id: "term",
      key: "create_term",
      title: "Create a Term",
      description: "Add one or more terms under your session (e.g., First Term, Second Term, Third Term). Each term helps you organize results, attendance, and grading per period.",
      completed: completedSteps?.create_term,
      action: () => setActiveModal("term")
    },
    {
      id: "teachers",
      key: "add_employees",
      title: "Add Teachers",
      description: "Before classes can be created, assign your teaching staff. Add teachers with their roles and subjects.",
      completed: completedSteps?.add_employees,
      action: () => setActiveModal("teachers")
    },
    {
      id: "classes",
      key: "create_classes",
      title: "Create Classes",
      description: "Now, create your school's classes and assign class teachers. Classes act as containers for students, subjects, and results.",
      completed: completedSteps?.create_classes,
      action: () => setActiveModal("classes")
    },
    {
      id: "students",
      key: "add_students",
      title: "Add Students",
      description: "Begin student onboarding by entering basic details like name, email, and admission number. Assign each student to a class and session.",
      completed: completedSteps?.add_students,
      action: () => navigate("/users/create-student")
    },
    {
      id: "guardians",
      key: "add_guardians",
      title: "Add Guardians",
      description: "Link each student to their parent or guardian. This allows for easier communication, payment tracking, and report sharing.",
      completed: completedSteps?.add_guardians,
      action: () => setActiveModal("guardians")
    },
    {
      id: "subjects",
      key: "add_subjects",
      title: "Add Subjects",
      description: "Set up the subjects taught in each class. These subjects will later be linked to results, obtainable scores, and grading systems.",
      completed: completedSteps?.add_subjects,
      action: () => setActiveModal("subjects")
    }
  ];

  // Check if a step should be disabled
  const isStepDisabled = (stepIndex) => {
    // If all steps are completed, none should be disabled
    if (firstIncompleteIndex === -1) return false;

    // Disable steps that come after the first incomplete step
    return stepIndex > firstIncompleteIndex;
  };

  const handleStartStep = (step, stepIndex) => {
    if (step?.action && !isStepDisabled(stepIndex)) {
      step?.action();
    }
  };

  const handleCloseModal = () => {
    setActiveModal(null);
  };

  const handleStepCompleted = (stepId) => {
    setCompletedSteps(prev => ({
      ...prev,
      [stepId]: true
    }));
    setActiveModal(null);
  };

  const handleModalSuccess = (stepKey) => {
    handleStepCompleted(stepKey);
    getOnboarding();
  };

  const calculateProgress = () => {
    const relevantSteps = Object?.keys(completedSteps || {})
      ?.filter(key => key !== "add_obtainable_scores")
      ?.map(key => (completedSteps || {})[key]);

    const completedCount = relevantSteps?.filter(Boolean)?.length;

    return Math?.round((completedCount / steps?.length) * 100) || 0;
  };

  useEffect(() => {
    getOnboarding();
  }, []);

  return (
    <Layout>
      {isloading ? (
        <PageLoader />
      ) : (
        <div className={classes.container}>
          <Grid
            container
            spacing={4}
            sx={{
              "@media (max-width: 1000px)": {
                flexWrap: "wrap-reverse"
              }
            }}
          >
            {/* Main Content - Steps */}
            <Grid item lg={8} md={7} sm={12} xs={12}>
              <div className={classes.leftContent}>
                {/* Header */}
                <div className={classes.header}>
                  <h1 className={classes.title}>Welcome to Shelf 21</h1>
                  <div className={classes.progressSection}>
                    <div className={classes.progressBar}>
                      <div
                        className={classes.progressFill}
                        style={{ width: `${calculateProgress()}%` }}
                      >
                      </div>
                    </div>
                    <div className={classes.progressText}>
                      {calculateProgress()}% Complete
                    </div>
                  </div>
                </div>

                {/* Steps List */}
                <div className={classes.stepsContainer}>
                  {steps?.map((step, index) => {
                    const disabled = isStepDisabled(index);

                    return (
                      <div key={index} className={classes.stepCard}>
                        <div className={classes.stepContent}>
                          <h3 className={classes.stepTitle}>{step?.title}</h3>
                          <p className={classes.stepDescription}>{step?.description}</p>
                          {/* {disabled && (
                            <p className={classes.disabledText}>
                              Complete previous steps to unlock this step
                            </p>
                          )} */}
                        </div>
                        <div className={classes.stepIcon}>
                          {step?.completed ? (
                            <Icon
                              icon="mdi:check-circle"
                              className={classes.completedIcon}
                            />
                          ) : (
                            <Button
                              variant={disabled ? "disable" : "primary"}
                              buttonSize="small"
                              buttonName="Start"
                              color={disabled ? "btndisable" : "btndefault"}
                              onClick={() => handleStartStep(step, index)}
                              disabled={disabled}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Grid>

            {/* Sidebar - Help Section */}
            <Grid item lg={4} md={5} sm={12} xs={12}>
              <div className={classes.sidebarCard}>
                <div className={classes.mobileVideoHeader}>
                  <div className={classes.headerText}>
                    <h3 className={classes.sidebarTitle}>Need help</h3>
                    <p className={classes.sidebarDescription}>
        Navigate your app as quickly as possible
                    </p>
                  </div>
                  <div
                    className={`${classes.playIconContainer} ${showVideo ? classes.playing : ""}`}
                    onClick={() => setShowVideo(!showVideo)}
                  >
                    <Icon
                      icon={showVideo ? "mdi:pause" : "mdi:play"}
                      width={24}
                      height={24}
                      className={classes.playIcon}
                    />
                  </div>
                </div>

                <div className={`${classes.videoWrapper} ${
                  showVideo ? classes.videoVisible : classes.videoHidden
                }`}
                >
                  <ReactPlayer
                    url="https://youtu.be/IcnoCespvF0"
                    width="100%"
                    height="300px"
                    controls={true}
                    playing={showVideo}
                    light={showVideo ? false : "https://img.youtube.com/vi/IcnoCespvF0/maxresdefault.jpg"}
                  />
                </div>
              </div>
            </Grid>
          </Grid>

          {/* Modals */}
          <CreateSessionModal
            isOpen={activeModal === "session"}
            onClose={handleCloseModal}
            onSuccess={() => handleModalSuccess("create_session")}
          />

          <CreateTermModal
            isOpen={activeModal === "term"}
            onClose={handleCloseModal}
            onSuccess={() => handleModalSuccess("create_term")}
          />

          <AddTeacherModal
            isOpen={activeModal === "teachers"}
            onClose={handleCloseModal}
            onSuccess={() => handleModalSuccess("add_employees")}
          />

          <CreateClassModal
            isOpen={activeModal === "classes"}
            onClose={handleCloseModal}
            onSuccess={() => handleModalSuccess("create_classes")}
          />

          <AddGuardianModal
            isOpen={activeModal === "guardians"}
            onClose={handleCloseModal}
            onSuccess={() => handleModalSuccess("add_guardians")}
          />

          <CreateSubjectModal
            isOpen={activeModal === "subjects"}
            onClose={handleCloseModal}
            onSuccess={() => handleModalSuccess("add_subjects")}
          />
        </div>
      )}
    </Layout>
  );
};

export default OnboardingGuide;
